import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useHistory, useParams } from 'react-router-dom'

import { fetchPointImage } from '../../../api/buildingFetches'
import {
  ProgressAiPointResult,
  ReviewQueueMeta,
  ReviewQueuePoint,
  getReviewTaskQueue,
  heartbeatReviewTask,
  listReviewTaskAiBatchPoints,
  setReviewTaskStatus,
} from '../../../api/reviewTasks'
import { Point } from '../../../api/types'
import iconZoomInBlue from '../../../assets/images/icon_zoom_in_blue.svg'
import iconZoomOutBlue from '../../../assets/images/icon_zoom_out_blue.svg'
import { useBuildingContext } from '../../../contexts/buildingContext'
import { useNotifications } from '../../../contexts/notificationProvider'
import { useGeneratedPaths } from '../../../hooks/useGeneratedPaths'
import { IconButton } from '../../common/IconButton'
import { LoadingIndicator } from '../../common/LoadingIndicator'
import { MapViewer } from '../../common/MapViewer'
import { ViewSelector } from '../../common/ViewSelector/ViewSelector'

import { ReviewPreviewPopup } from './ReviewPreviewPopup'
import { ReviewSequencePath } from './ReviewSequencePath'
import {
  aiSuggestionStopProgress,
  buildAiSuggestionNavStops,
  filterQueueToSuggestionPoints,
  firstRemainingFloorCode,
  firstRemainingQueuePoint,
  formatDurationHoursMinutes,
  formatMinutesPerPoint,
  isReviewQueueFinished,
  suggestionViewpointPkSet,
} from './reviewTaskFormat'
import {
  ActionButton,
  FloorBackButton,
  FloorViewControls,
  FloorViewMap,
  FloorViewRoot,
  ReviewSubmissionsButton,
  StartContinueButton,
  StubWrap,
  TaskProgressBody,
  TaskProgressRow,
} from './styles'
import { usePendingReviewTaskCount } from './usePendingReviewTaskCount'
import { useReviewSessionTime } from './useReviewSessionTime'
import { useReviewTaskClaim } from './useReviewTaskClaim'

const previewUrlFromImages = (
  images: Array<{
    id?: number
    sub_point_id?: number
    stitched_small_image_url?: string | null
    processed_image_url?: string
    stitched_org_image_url?: string
    taken_on?: string
  }>,
  entry: ReviewQueuePoint,
) => {
  const match =
    images.find(image => String(image.id) === String(entry.latest_image_id)) ||
    images.find(image => String(image.sub_point_id) === String(entry.point_id))
  if (!match) {
    return { url: null as string | null, date: entry.latest_taken_on }
  }
  return {
    url:
      match.stitched_small_image_url ||
      match.processed_image_url ||
      match.stitched_org_image_url ||
      null,
    date: match.taken_on || entry.latest_taken_on,
  }
}

export const ReviewTaskFloorView = () => {
  const history = useHistory()
  const { taskId, floorId } = useParams<{ taskId: string; floorId?: string }>()
  const {
    generateReviewOverviewPath,
    generateReviewTaskFloorPath,
    generateReviewTaskPointPath,
  } = useGeneratedPaths()
  const { addNotification } = useNotifications()
  const { state: buildingState } = useBuildingContext()
  const { adjustPendingReviewTaskCount } = usePendingReviewTaskCount()
  const projectId = buildingState.projectId as string
  const mapRef = useRef<MapViewer | null>(null)

  const [queue, setQueue] = useState<ReviewQueuePoint[]>([])
  const [meta, setMeta] = useState<ReviewQueueMeta | null>(null)
  const [loadingQueue, setLoadingQueue] = useState(true)
  const [queueError, setQueueError] = useState(false)
  const [aiPointResults, setAiPointResults] = useState<ProgressAiPointResult[]>(
    [],
  )
  const [aiAssistPointsReady, setAiAssistPointsReady] = useState(false)
  const [loadedImg, setLoadedImg] = useState('')
  const [progressExpanded, setProgressExpanded] = useState(true)
  const [selectedPreviewPointId, setSelectedPreviewPointId] = useState<
    string | null
  >(null)
  const [fetchedPreview, setFetchedPreview] = useState<{
    pointId: string
    url: string | null
  } | null>(null)

  const loadQueue = useCallback(() => {
    if (!projectId || !taskId) {
      return
    }
    setLoadingQueue(true)
    setQueueError(false)
    getReviewTaskQueue(projectId, taskId)
      .then(response => {
        setQueue(response.data || [])
        setMeta(response.meta)
      })
      .catch(() => {
        setQueue([])
        setQueueError(true)
        addNotification('Unable to load review queue', 'error')
      })
      .finally(() => {
        setLoadingQueue(false)
      })
  }, [addNotification, projectId, taskId])

  useEffect(() => {
    loadQueue()
  }, [loadQueue])

  useEffect(() => {
    if (!projectId || !taskId || !meta?.ai_assist) {
      setAiPointResults([])
      setAiAssistPointsReady(false)
      return
    }
    let cancelled = false
    setAiAssistPointsReady(false)
    listReviewTaskAiBatchPoints(projectId, Number(taskId))
      .then(result => {
        if (!cancelled) {
          setAiPointResults(result.points || [])
          setAiAssistPointsReady(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAiPointResults([])
          setAiAssistPointsReady(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [meta?.ai_assist, projectId, taskId])

  const aiAssistNavActive = Boolean(meta?.ai_assist && aiAssistPointsReady)
  const reviewQueue = useMemo(
    () =>
      aiAssistNavActive
        ? filterQueueToSuggestionPoints(
            queue,
            suggestionViewpointPkSet(aiPointResults),
          )
        : queue,
    [aiAssistNavActive, aiPointResults, queue],
  )

  const suggestionStops = useMemo(
    () =>
      aiAssistNavActive
        ? buildAiSuggestionNavStops(
            queue,
            meta?.trackers || [],
            aiPointResults,
          )
        : [],
    [aiAssistNavActive, aiPointResults, meta?.trackers, queue],
  )
  const suggestionProgress = useMemo(
    () => aiSuggestionStopProgress(suggestionStops, queue),
    [queue, suggestionStops],
  )

  const {
    claimToken,
    claimedActiveSeconds,
    setSecondsProvider,
  } = useReviewTaskClaim(projectId, taskId, {
    disabled: meta?.status === 'completed',
  })

  const { sessionSeconds, getAccumulatedSeconds } = useReviewSessionTime(
    projectId,
    taskId,
    'active',
    {
      disabled: meta?.status === 'completed',
      initialSeconds: claimedActiveSeconds || meta?.active_seconds,
      onFlush: seconds => {
        if (!claimToken || !projectId || !taskId) {
          return
        }
        void heartbeatReviewTask(projectId, taskId, {
          claim_token: claimToken,
          accumulated_seconds: seconds,
        }).catch(() => undefined)
      },
    },
  )

  useEffect(() => {
    setSecondsProvider(getAccumulatedSeconds)
  }, [getAccumulatedSeconds, setSecondsProvider])

  useEffect(() => {
    if (loadingQueue || !reviewQueue.length || !projectId || floorId) {
      return
    }
    const nextFloor = firstRemainingFloorCode(reviewQueue)
    if (nextFloor) {
      history.replace(generateReviewTaskFloorPath(projectId, taskId, nextFloor))
    }
  }, [
    floorId,
    generateReviewTaskFloorPath,
    history,
    loadingQueue,
    projectId,
    reviewQueue,
    taskId,
  ])

  const currentFloorCode = floorId || buildingState.floorId
  const floorQueue = useMemo(
    () => reviewQueue.filter(entry => entry.floor_code === currentFloorCode),
    [currentFloorCode, reviewQueue],
  )
  const visitedByPoint = useMemo(() => {
    const map = new Map<string, ReviewQueuePoint>()
    floorQueue.forEach(entry => {
      map.set(String(entry.point_id), entry)
    })
    return map
  }, [floorQueue])

  const resumePoint = useMemo(() => {
    return (
      floorQueue.find(entry => !entry.visited_action) ||
      firstRemainingQueuePoint(reviewQueue) ||
      floorQueue[0] ||
      null
    )
  }, [floorQueue, reviewQueue])

  useEffect(() => {
    setSelectedPreviewPointId(null)
  }, [currentFloorCode])

  const previewEntry = useMemo(() => {
    if (selectedPreviewPointId) {
      return visitedByPoint.get(selectedPreviewPointId) || resumePoint
    }
    return resumePoint
  }, [resumePoint, selectedPreviewPointId, visitedByPoint])

  const floorImages = buildingState.floorData?.images
  const floorPreview = useMemo(() => {
    if (!previewEntry) {
      return {
        url: null as string | null,
        date: null as string | null,
        waiting: true,
      }
    }
    if (!Array.isArray(floorImages)) {
      return { url: null, date: previewEntry.latest_taken_on, waiting: true }
    }
    const result = previewUrlFromImages(floorImages, previewEntry)
    return { ...result, waiting: false }
  }, [floorImages, previewEntry])

  useEffect(() => {
    if (
      !previewEntry ||
      !projectId ||
      floorPreview.waiting ||
      floorPreview.url
    ) {
      return
    }
    if (!previewEntry.latest_image_id) {
      setFetchedPreview({
        pointId: String(previewEntry.point_id),
        url: null,
      })
      return
    }
    let cancelled = false
    fetchPointImage(
      projectId,
      previewEntry.floor_code,
      String(previewEntry.latest_image_id),
    ).then(data => {
      if (cancelled) {
        return
      }
      setFetchedPreview({
        pointId: String(previewEntry.point_id),
        url:
          data?.stitched_small_image_url ||
          data?.processed_image_url ||
          data?.stitched_org_image_url ||
          null,
      })
    })
    return () => {
      cancelled = true
    }
  }, [floorPreview.url, floorPreview.waiting, previewEntry, projectId])

  const previewImageUrl =
    floorPreview.url ||
    (fetchedPreview?.pointId === String(previewEntry?.point_id)
      ? fetchedPreview.url
      : null)
  const previewLoading =
    !!previewEntry &&
    !previewImageUrl &&
    (floorPreview.waiting || !!previewEntry.latest_image_id)

  const mapPoints = useMemo(() => {
    return (buildingState.floorData?.points || [])
      .filter((point: Point) => visitedByPoint.has(String(point.point_id)))
      .map((point: Point) => ({
        ...point,
        severityClass: visitedByPoint.get(String(point.point_id))
          ?.visited_action
          ? 'back_green'
          : undefined,
      }))
  }, [buildingState.floorData?.points, visitedByPoint])

  const sequenceIds = useMemo(
    () => floorQueue.map(entry => entry.point_id),
    [floorQueue],
  )

  const goToQueuePoint = useCallback(
    (entry: ReviewQueuePoint) => {
      history.push(
        generateReviewTaskPointPath(
          projectId,
          taskId,
          entry.floor_code,
          String(entry.point_id),
        ),
      )
    },
    [generateReviewTaskPointPath, history, projectId, taskId],
  )

  const selectPreviewPoint = useCallback(
    (pointId: string | number) => {
      const entry = visitedByPoint.get(String(pointId))
      if (!entry) {
        return
      }
      setSelectedPreviewPointId(String(pointId))
    },
    [visitedByPoint],
  )

  const goToPoint = useCallback(
    (pointId: string | number) => {
      const entry = visitedByPoint.get(String(pointId))
      if (!entry) {
        return
      }
      goToQueuePoint(entry)
    },
    [goToQueuePoint, visitedByPoint],
  )

  const startPoint = firstRemainingQueuePoint(reviewQueue)
  const startLabel = reviewQueue.some(entry => entry.visited_action)
    ? 'Continue'
    : 'Start'
  const queueFinished = isReviewQueueFinished(reviewQueue)

  const markTaskComplete = async () => {
    if (!projectId || !taskId) {
      return
    }
    try {
      await setReviewTaskStatus(projectId, Number(taskId), 'completed')
      adjustPendingReviewTaskCount(-1)
      history.push(generateReviewOverviewPath(projectId))
    } catch {
      addNotification('Unable to mark task complete', 'error')
    }
  }

  const viewed = aiAssistNavActive
    ? suggestionProgress.viewed
    : meta?.points_viewed ?? queue.filter(entry => entry.visited_action).length
  const total = aiAssistNavActive
    ? suggestionProgress.total
    : meta?.points_total ?? queue.length
  const percent = total ? Math.round((viewed / total) * 100) : 0
  const imageUrl = buildingState.floorData?.imageUrl
  const highlightedPointId =
    selectedPreviewPointId ||
    (resumePoint?.floor_code === currentFloorCode
      ? String(resumePoint.point_id)
      : undefined)

  if (loadingQueue) {
    return <LoadingIndicator />
  }

  if (queueError) {
    return (
      <StubWrap>
        <h1>Unable to load review queue.</h1>
        <ActionButton type="button" onClick={() => loadQueue()}>
          Retry
        </ActionButton>
      </StubWrap>
    )
  }

  if (!queue.length || (aiAssistNavActive && !reviewQueue.length)) {
    return (
      <StubWrap>
        <h1>
          {aiAssistNavActive
            ? 'No points with AI suggestions to review.'
            : 'No remaining items to review.'}
        </h1>
        <ActionButton
          type="button"
          onClick={() => history.push(generateReviewOverviewPath(projectId))}
        >
          Back to task list
        </ActionButton>
      </StubWrap>
    )
  }

  if (!floorId) {
    return <LoadingIndicator />
  }

  return (
    <FloorViewRoot>
      <FloorViewMap>
        {!imageUrl && <LoadingIndicator />}
        {imageUrl && (
          <MapViewer
            ref={mapRef}
            image={imageUrl}
            setLoadedImg={setLoadedImg}
            selectedDateRange={null}
            imageLoaded={loadedImg === imageUrl}
            points={mapPoints}
            highlightedPointId={highlightedPointId}
            allPointsActive
            onClickMapPoint={point => selectPreviewPoint(point.point_id)}
            onDoubleClickMapPoint={point => goToPoint(point.point_id)}
          >
            <ReviewSequencePath
              sequence={sequenceIds}
              points={mapPoints}
              width={5}
            />
          </MapViewer>
        )}
        <IconButton
          icon={iconZoomInBlue}
          size={40}
          primary={false}
          onClick={() => mapRef.current?.zoomIn()}
          style={{
            position: 'fixed',
            right: 'max(16px, env(safe-area-inset-right, 0px))',
            top: 'calc(50% - 44px - env(safe-area-inset-top, 0px))',
            zIndex: 2,
          }}
        />
        <IconButton
          icon={iconZoomOutBlue}
          size={40}
          primary={false}
          onClick={() => mapRef.current?.zoomOut()}
          style={{
            position: 'fixed',
            right: 'max(16px, env(safe-area-inset-right, 0px))',
            top: 'calc(50% + 4px - env(safe-area-inset-top, 0px))',
            zIndex: 2,
          }}
        />
      </FloorViewMap>
      <FloorViewControls>
        <FloorBackButton
          type="button"
          onClick={() => history.push(generateReviewOverviewPath(projectId))}
        >
          ← Back
        </FloorBackButton>
        <ViewSelector
          title="Task Progress"
          initialViewSelectorExpanded
          viewingExpanded={progressExpanded}
          setViewingExpanded={setProgressExpanded}
          fitContent
        >
          <TaskProgressBody>
            <TaskProgressRow>
              <span>Completion</span>
              <span>
                {viewed}/{total} · {percent}%
              </span>
            </TaskProgressRow>
            <TaskProgressRow>
              <span>Session time</span>
              <span>{formatDurationHoursMinutes(sessionSeconds)}</span>
            </TaskProgressRow>
            <TaskProgressRow>
              <span>Performance</span>
              <span>{formatMinutesPerPoint(sessionSeconds, viewed)}</span>
            </TaskProgressRow>
            {queueFinished ? (
              <>
                <StartContinueButton type="button" onClick={markTaskComplete}>
                  Mark complete
                </StartContinueButton>
                <ReviewSubmissionsButton
                  type="button"
                  disabled={!startPoint}
                  onClick={() => startPoint && goToQueuePoint(startPoint)}
                >
                  Review submissions
                </ReviewSubmissionsButton>
              </>
            ) : (
              <StartContinueButton
                type="button"
                disabled={!startPoint}
                onClick={() => startPoint && goToQueuePoint(startPoint)}
              >
                {startLabel}
              </StartContinueButton>
            )}
          </TaskProgressBody>
        </ViewSelector>
      </FloorViewControls>
      <ReviewPreviewPopup
        date={floorPreview.date}
        imageUrl={previewImageUrl}
        loading={previewLoading}
        onExpand={() => previewEntry && goToQueuePoint(previewEntry)}
      />
    </FloorViewRoot>
  )
}
